/* eslint-disable @typescript-eslint/no-explicit-any */
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICheckoutComponent} from '@app/checkout/models/checkout-component.model';
import {Order} from '@app/checkout/models/order-request.model';
import {ISampleType} from '@app/checkout/models/sample-type.model';
import {
  getOrder,
  getSampleTypesFromPatientInfoPage,
  resetCheckoutStatus,
  updateOrderFromClinicalInfo,
} from '@app/checkout/state/checkout.actions';
import {selectCheckoutStatus, selectOrderForm, selectSampleTypes} from '@app/checkout/state/checkout.selector';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {EActionStatus} from '@app/shared/enums/action-status.enum';
import {ERestriction, IDynamicFormFieldType} from '@app/shared/models/dynamic.model';
import {StepperService} from '@app/shared/services/stepper.service';
import {selectSelectedLanguage} from '@app/shared/state/language/language.selector';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {Subject, takeUntil, tap} from 'rxjs';
import {DynamicFormComponent} from '../../../shared/components/dynamic-form/dynamic-form.component';

@Component({
  selector: 'app-clinical-info',
  templateUrl: './clinical-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DynamicFormComponent],
})
export class ClinicalInfoComponent implements OnDestroy, ICheckoutComponent {
  private readonly store = inject(Store);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly stepperService = inject(StepperService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  orderId = 0;
  orderData: Order | undefined;
  private readonly unsubscribe$ = new Subject<void>();
  checkoutState: ECheckoutStatus | undefined;
  checkoutStatus = ECheckoutStatus;
  actionStatus = EActionStatus;
  leftLabels = true;
  readonly = false;
  dynamicForm!: FormGroup<any>;
  dynamicFormValues: any;
  today = new Date();
  formTemplateJson = '';
  formValuesJson = '';
  nextInProgress = false;
  previousInProgress = false;
  cancelInProgress = false;
  draftInProgress = false;
  private selectedOrderItemIndex = 0;
  globalToast = 'global-toast';
  private sampleTypes: ISampleType[] = [];
  private lastSampleTypesProductId: number | null = null;
  private shouldMarkClinicalFormAsDirty = false;
  private hasSanitizedClinicalHistoryValuesChange = false;

  constructor() {
    this.route.queryParams?.subscribe(params => {
      if (params['id'] && params['id'] > 0) {
        this.orderId = params['id'];
      }
    });

    this.store
      .select(selectSelectedLanguage)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(() => {
          this.store.dispatch(getOrder({orderId: this.orderId}));
        })
      )
      .subscribe();

    this.listenToSampleTypes();
    this.getOrderData();
    this.getCheckoutStatus();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  dynamicFormChange(dynamicFormValue: any) {
    const sanitizedChange = this.getSanitizedDynamicFormValue(dynamicFormValue);
    this.dynamicFormValues = sanitizedChange.value;

    if (sanitizedChange.wasUpdated) {
      this.formValuesJson = sanitizedChange.serializedValue;
      this.hasSanitizedClinicalHistoryValuesChange = true;
      this.shouldMarkClinicalFormAsDirty = true;

      if (this.dynamicForm) {
        this.dynamicForm.markAsDirty();
      }
    }

    this.cdr.markForCheck();
  }

  dynamicFormStatus(dynamicForm: FormGroup<any>) {
    this.dynamicForm = dynamicForm;

    if (this.shouldMarkClinicalFormAsDirty) {
      this.dynamicForm.markAsDirty();
      this.shouldMarkClinicalFormAsDirty = false;
    }

    this.cdr.markForCheck();
  }

  disableNext() {
    this.cdr.detectChanges();
    return !this.dynamicForm?.valid;
  }

  next() {
    const lastCompletedStep = this.orderData?.lastCompletedStep ?? -1;
    if (
      this.dynamicForm?.dirty ||
      this.hasSanitizedClinicalHistoryValuesChange ||
      lastCompletedStep < CheckoutStep.ClinicalHistory
    ) {
      this.nextInProgress = true;
      this.store.dispatch(resetCheckoutStatus());
      this.saveOrder();
    } else {
      this.stepperService.emitNext(CheckoutStep.ConsentInfo);
    }
  }

  previous() {
    this.stepperService.emitPrevious(CheckoutStep.PatientInfo);
  }

  cancelOrder() {
    // use for custom cancel logic
  }

  saveDraftOrder() {
    if (
      this.orderData &&
      this.orderData.lastCompletedStep >= CheckoutStep.ClinicalHistory &&
      this.dynamicForm.invalid
    ) {
      ErrorHandlerUtils.showErrorMessage(
        this.messageService,
        this.globalToast,
        this.translate.instant('Checkout.InvalidFormMessage')
      );
      return;
    }
    this.draftInProgress = true;
    this.saveOrder(true);
  }

  nextLoading() {
    return this.nextInProgress;
  }

  previousLoading() {
    return this.previousInProgress;
  }

  cancelLoading() {
    return this.cancelInProgress;
  }

  draftOrSubmitLoading() {
    return this.draftInProgress;
  }

  saveOrder(isDraft = false) {
    if (this.orderData) {
      const lastCompletedStep = this.orderData?.lastCompletedStep ?? -1;
      const orderItems = [...this.orderData.orderItems];
      let orderItem = orderItems[this.selectedOrderItemIndex];
      orderItem = {
        ...orderItem,
        clinicalHistoryValues: JSON.stringify(this.dynamicFormValues, (key, value) => {
          return value === null ? undefined : value;
        }),
      };
      orderItems[this.selectedOrderItemIndex] = orderItem;

      const lastCompletedStepDraftCheck = isDraft ? CheckoutStep.PatientInfo : CheckoutStep.ClinicalHistory;
      this.orderData = {
        ...this.orderData,
        productId: this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId,
        orderStatusId: OrderStatusEnum.New.toString(),
        orderItems: orderItems,
        lastCompletedStep:
          lastCompletedStep > CheckoutStep.ClinicalHistory ? lastCompletedStep : lastCompletedStepDraftCheck,
        currentStep: isDraft ? CheckoutStep.ClinicalHistory : CheckoutStep.ConsentInfo,
      };
    }

    const orderData = {...this.orderData} as Order;
    this.store.dispatch(
      updateOrderFromClinicalInfo({
        orderRequest: orderData,
        isDraft: isDraft,
      })
    );

    this.hasSanitizedClinicalHistoryValuesChange = false;
  }

  private getOrderData() {
    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.orderData = order;
          if (order.orderItems?.length) {
            const selectedOrderItem = order.orderItems[this.selectedOrderItemIndex];
            this.dispatchForSampleTypes();
            this.formTemplateJson = this.enrichClinicalHistoryTemplate(selectedOrderItem.clinicalHistoryTemplate);
            this.formValuesJson = selectedOrderItem.clinicalHistoryValues;
            const selectedOrderItemAttribute = selectedOrderItem.orderItemAttributes?.find(
              x => x.orderItemId == selectedOrderItem.id
            );
            this.readonly = !(selectedOrderItem.isActive && selectedOrderItemAttribute?.canBeOrdered);
          }
        })
      )
      .subscribe();
  }

  private listenToSampleTypes() {
    this.store
      .select(selectSampleTypes)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap(sampleTypes => {
          this.sampleTypes = sampleTypes ?? [];
          this.updateClinicalHistoryTemplate();
        })
      )
      .subscribe();
  }

  private getCheckoutStatus() {
    this.store
      .select(selectCheckoutStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((checkoutStatus: ECheckoutStatus) => {
          this.checkoutState = checkoutStatus;

          if (checkoutStatus === ECheckoutStatus.SuccessFromClinicalInfo) {
            this.dynamicForm?.markAsPristine();
            this.stepperService.emitNext(CheckoutStep.ConsentInfo);
          }
          if (
            checkoutStatus === ECheckoutStatus.SuccessFromClinicalInfo ||
            checkoutStatus === ECheckoutStatus.ErrorFromClinicalInfo
          ) {
            this.nextInProgress = false;
            this.draftInProgress = false;
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();
  }

  private dispatchForSampleTypes() {
    const productId = this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId;

    if (!productId) {
      return;
    }

    if (this.lastSampleTypesProductId === productId) {
      return;
    }

    this.lastSampleTypesProductId = productId;

    this.store.dispatch(getSampleTypesFromPatientInfoPage({productId}));
  }

  private updateClinicalHistoryTemplate() {
    const selectedOrderItem = this.orderData?.orderItems?.[this.selectedOrderItemIndex];

    if (!selectedOrderItem) {
      return;
    }

    this.formTemplateJson = this.enrichClinicalHistoryTemplate(selectedOrderItem.clinicalHistoryTemplate);
    const sourceValues = this.formValuesJson || selectedOrderItem.clinicalHistoryValues;
    this.formValuesJson = sourceValues;
    this.cdr.markForCheck();
  }

  private enrichClinicalHistoryTemplate(clinicalHistoryTemplate?: string): string {
    if (!clinicalHistoryTemplate) {
      return '';
    }

    try {
      const parsedTemplate = JSON.parse(clinicalHistoryTemplate) as IDynamicFormFieldType[];
      this.updateUtissNoSlidesVisibility(parsedTemplate);

      return JSON.stringify(parsedTemplate);
    } catch {
      return clinicalHistoryTemplate;
    }
  }

  private updateUtissNoSlidesVisibility(fields: IDynamicFormFieldType[]) {
    for (const field of fields) {
      if (field.name === 'utiss_no_slides') {
        field.hidden = !this.isUtissSampleTypeSelected();
        if (field.hidden === false) {
          field.validators = [{restriction: ERestriction.Required}, {restriction: ERestriction.Min, value: '1'}];
        }
      }

      const nestedFields = (field as {fields?: IDynamicFormFieldType[]}).fields;
      if (nestedFields?.length) {
        this.updateUtissNoSlidesVisibility(nestedFields);
      }
    }
  }

  private isUtissSampleTypeSelected(): boolean {
    const selectedSampleType = this.getSelectedSampleType();

    return selectedSampleType?.name?.toUpperCase() === 'UTISS';
  }

  private getSelectedSampleType(): ISampleType | undefined {
    const sampleTypeId = this.orderData?.sample?.sampleTypeId ?? this.orderData?.patientSampleTypeId;
    return this.sampleTypes.find(sampleType => sampleType.id === sampleTypeId);
  }

  private updateSelectedOrderItemClinicalHistoryValues(clinicalHistoryValues: string): void {
    const selectedOrderItem = this.orderData?.orderItems?.[this.selectedOrderItemIndex];

    if (!selectedOrderItem || !this.orderData?.orderItems?.length) {
      return;
    }

    const orderItems = [...this.orderData.orderItems];
    orderItems[this.selectedOrderItemIndex] = {
      ...selectedOrderItem,
      clinicalHistoryValues,
    };

    this.orderData = {
      ...this.orderData,
      orderItems,
    };
  }

  private tryParseJson(jsonValue: string): unknown {
    try {
      return JSON.parse(jsonValue);
    } catch {
      return null;
    }
  }

  private getSanitizedDynamicFormValue(dynamicFormValue: any): {
    value: any;
    serializedValue: string;
    wasUpdated: boolean;
  } {
    const serializedValue = JSON.stringify(dynamicFormValue ?? {});
    const selectedSampleType = this.getSelectedSampleType();
    const isUtissSampleType = selectedSampleType?.name?.toUpperCase() === 'UTISS';

    if (!selectedSampleType || isUtissSampleType) {
      return {
        value: dynamicFormValue,
        serializedValue,
        wasUpdated: false,
      };
    }

    const sanitizedResult = this.removeFieldFromJson(serializedValue, 'utiss_no_slides');
    return {
      value: this.tryParseJson(sanitizedResult.value) ?? dynamicFormValue,
      serializedValue: sanitizedResult.value,
      wasUpdated: sanitizedResult.wasUpdated,
    };
  }

  private getSanitizedClinicalHistoryValues(clinicalHistoryValues?: string): string {
    return this.getSanitizedClinicalHistoryValuesResult(clinicalHistoryValues).value;
  }

  private getSanitizedClinicalHistoryValuesResult(clinicalHistoryValues?: string): {
    value: string;
    wasUpdated: boolean;
  } {
    if (!clinicalHistoryValues) {
      return {value: '', wasUpdated: false};
    }

    const selectedSampleType = this.getSelectedSampleType();
    const isUtissSampleType = selectedSampleType?.name?.toUpperCase() === 'UTISS';

    if (!selectedSampleType) {
      return {
        value: this.formValuesJson || clinicalHistoryValues,
        wasUpdated: false,
      };
    }

    if (isUtissSampleType) {
      return {value: clinicalHistoryValues, wasUpdated: false};
    }

    return this.removeFieldFromJson(clinicalHistoryValues, 'utiss_no_slides');
  }

  private removeFieldFromJson(
    jsonValue: string,
    fieldName: string
  ): {
    value: string;
    wasUpdated: boolean;
  } {
    try {
      const parsedValue = JSON.parse(jsonValue);
      const sanitizedResult = this.removeFieldRecursively(parsedValue, fieldName);
      return {
        value: JSON.stringify(sanitizedResult.value),
        wasUpdated: sanitizedResult.wasUpdated,
      };
    } catch {
      return {value: jsonValue, wasUpdated: false};
    }
  }

  private removeFieldRecursively(
    value: unknown,
    fieldName: string
  ): {
    value: unknown;
    wasUpdated: boolean;
  } {
    if (Array.isArray(value)) {
      let wasUpdated = false;
      const sanitizedArray = value.map(item => {
        const nestedResult = this.removeFieldRecursively(item, fieldName);
        if (nestedResult.wasUpdated) {
          wasUpdated = true;
        }
        return nestedResult.value;
      });
      return {value: sanitizedArray, wasUpdated};
    }

    if (value && typeof value === 'object') {
      let wasUpdated = false;
      const sanitizedObjectEntries: [string, unknown][] = [];

      for (const [key, nestedValue] of Object.entries(value)) {
        if (key === fieldName) {
          wasUpdated = true;
          continue;
        }

        const nestedResult = this.removeFieldRecursively(nestedValue, fieldName);
        if (nestedResult.wasUpdated) {
          wasUpdated = true;
        }
        sanitizedObjectEntries.push([key, nestedResult.value]);
      }

      return {
        value: Object.fromEntries(sanitizedObjectEntries),
        wasUpdated,
      };
    }

    return {value, wasUpdated: false};
  }
}
